import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import AccountAPI from '../../axios/account';
import { PanelIcon } from '../user-panel/user-panel';
import { PanelPagination, PanelConfirm, Check } from '../user-panel/components/panel-ui';

const ROWS_PER_PAGE = 10;
// highlighted as the recommended package
const POPULAR_PACKAGE = 1000;
// marks the amount the user types in instead of a package
const CUSTOM = 'custom';
const CUSTOM_STEP = 100;

const money = (value) => (Number(value) || 0).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const credits = (value) => (Number(value) || 0).toLocaleString('sr-RS');

export const TransactionPage = () => {
    const { t } = useTranslation(["account"]);
    const { refreshCounts } = useOutletContext() || {};
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [packages, setPackages] = useState([]);
    const [topUpEnabled, setTopUpEnabled] = useState(false);
    const [limits, setLimits] = useState({ min: 0, max: 0 });
    const [selected, setSelected] = useState(POPULAR_PACKAGE);
    const [customAmount, setCustomAmount] = useState('');
    const customInputRef = useRef(null);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    // { type: 'success' | 'error', text }
    const [notice, setNotice] = useState(null);

    const fetchTransactions = useCallback(async () => {
        try {
            const [resTransactions, resBalance] = await Promise.all([
                AccountAPI.getTransactions(),
                AccountAPI.getBalance()
            ]);
            setRows(resTransactions.data);
            setBalance(resBalance.data.result);
            setLoaded(true);
        } catch (error) {
            console.log(error);
        }
    }, []);

    useEffect(() => {
        fetchTransactions();
        AccountAPI.getCreditPackages().then(res => {
            setPackages(res.data.packages);
            setLimits({ min: res.data.min, max: res.data.max });
            setTopUpEnabled(res.data.enabled);
            if (!res.data.packages.includes(POPULAR_PACKAGE)) setSelected(res.data.packages[0]);
        }).catch(err => console.log(err));
    }, [fetchTransactions]);

    const amount = selected === CUSTOM ? Number(customAmount) || 0 : selected;
    const amountValid = amount >= limits.min && amount <= limits.max;

    const pickCustom = () => {
        setSelected(CUSTOM);
        customInputRef.current?.focus();
    };

    // the +/− buttons move the typed amount by one step, staying within the limits
    const stepCustom = (direction) => {
        const current = Number(customAmount) || 0;
        const next = current ? current + direction * CUSTOM_STEP : limits.min;
        setCustomAmount(String(Math.min(limits.max, Math.max(limits.min, next))));
        setSelected(CUSTOM);
    };

    const topUp = async () => {
        setBusy(true);
        setNotice(null);
        try {
            const res = await AccountAPI.topUp(amount);
            setBalance(res.data.result);
            setNotice({ type: 'success', text: t('top_up_success', { amount: credits(amount) }) });
            setCustomAmount('');
            setPage(1);
            await fetchTransactions();
            if (refreshCounts) refreshCounts();
        } catch (error) {
            console.log(error);
            const disabled = error.response?.data?.message === 'top_up_disabled';
            setNotice({ type: 'error', text: t(disabled ? 'top_up_disabled' : 'top_up_error') });
        }
        setBusy(false);
    };

    const describe = (description) => description ? t(`tx_${description}`, { defaultValue: description }) : '';

    const pageCount = Math.ceil(rows.length / ROWS_PER_PAGE);
    const pageRows = rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

    return (
        <section className='hs hs-panel-page'>
            <div className='hs-panel-balance'>
                <span className='hs-panel-balance-label'>{t('balance_label')}</span>
                <span className='hs-panel-balance-value'>{money(balance)} <small>RSD</small></span>
            </div>

            {packages.length > 0 &&
                <div className='hs-panel-card hs-panel-form'>
                    <div className='hs-panel-form-head'>
                        <span className='hs-panel-form-icon blue'><PanelIcon name='credit' /></span>
                        <h2>{t('top_up_title')}</h2>
                    </div>
                    <p className='hs-panel-form-intro'>{t('top_up_text')}</p>

                    <div className='hs-panel-credit-packages' role='radiogroup' aria-label={t('top_up_title')}>
                        {packages.map(amount => (
                            <button key={amount} role='radio' aria-checked={amount === selected} disabled={!topUpEnabled}
                                className={`hs-panel-credit-package ${amount === selected ? 'on' : ''}`}
                                onClick={() => setSelected(amount)}>
                                {amount === POPULAR_PACKAGE && <span className='hs-panel-credit-badge'>{t('popular')}</span>}
                                <span className='hs-panel-credit-check'><Check size={12} /></span>
                                <span className='hs-panel-credit-amount'>{credits(amount)}</span>
                                <span className='hs-panel-credit-unit'>{t('credits')}</span>
                                <span className='hs-panel-credit-price'>{credits(amount)} RSD</span>
                            </button>
                        ))}
                    </div>

                    <div className='hs-panel-credit-or'><span>{t('or')}</span></div>

                    <div className='hs-panel-credit-custom'>
                        <label className='hs-panel-field-label' htmlFor='credit-custom-amount'>{t('custom_amount')}</label>
                        <div className={`hs-panel-credit-custom-row ${selected === CUSTOM ? 'on' : ''} ${topUpEnabled ? '' : 'disabled'}`}
                            onClick={() => topUpEnabled && pickCustom()}>
                            <input id='credit-custom-amount' ref={customInputRef} className='hs-panel-credit-input' inputMode='numeric'
                                placeholder={t('custom_placeholder')} disabled={!topUpEnabled} value={customAmount ? credits(customAmount) : ''}
                                onFocus={() => setSelected(CUSTOM)}
                                onChange={(e) => setCustomAmount(e.target.value.replace(/\D/g, '').slice(0, 7))} />
                            <span className='hs-panel-credit-unit'>{t('credits')}</span>
                            <button className='hs-panel-credit-step' aria-label={t('decrease')}
                                disabled={!topUpEnabled || !customAmount || Number(customAmount) <= limits.min}
                                onClick={(e) => { e.stopPropagation(); stepCustom(-1); }}>−</button>
                            <button className='hs-panel-credit-step' aria-label={t('increase')}
                                disabled={!topUpEnabled || Number(customAmount) >= limits.max}
                                onClick={(e) => { e.stopPropagation(); stepCustom(1); }}>+</button>
                        </div>
                        <span className='hs-panel-row-sub'>{t('custom_range', { min: credits(limits.min), max: credits(limits.max) })}</span>
                    </div>

                    {notice &&
                        <p className={`hs-panel-credit-notice ${notice.type}`} role='status'>
                            {notice.type === 'success' && <Check size={14} />}{notice.text}
                        </p>}

                    <div className='hs-panel-credit-footer'>
                        <span className='hs-panel-row-sub'>
                            {topUpEnabled
                                ? amountValid
                                    ? <>{t('credit_rate')} · {t('after_top_up', { amount: money(Number(balance) + amount) })}</>
                                    : t('custom_limits_hint', { min: credits(limits.min), max: credits(limits.max) })
                                : t('top_up_disabled')}
                        </span>
                        <button className='hs-panel-primary' disabled={!topUpEnabled || busy || !amountValid} onClick={() => setConfirming(true)}>
                            {amountValid ? t('top_up_button', { amount: credits(amount) }) : t('top_up_title')}
                        </button>
                    </div>
                </div>}

            {confirming &&
                <PanelConfirm title={t('confirm_title')} message={t('confirm_text', { amount: credits(amount) })}
                    confirmLabel={t('confirm_button')} onConfirm={topUp} onClose={() => setConfirming(false)} />}

            {loaded && rows.length === 0 &&
                <div className='hs-panel-card hs-panel-empty'>
                    <span className='hs-panel-empty-icon'><PanelIcon name='credit' size={24} /></span>
                    <h2>{t('no_transactions')}</h2>
                    <p>{t('no_transactions_text')}</p>
                </div>}

            {rows.length > 0 &&
                <div className='hs-panel-card hs-panel-form'>
                    <h2>{t('page_title')}</h2>
                    <div className='hs-panel-transactions'>
                        {pageRows.map((row, index) => (
                            <div className='hs-panel-transaction' key={`${row.date}-${index}`}>
                                <span className={`hs-panel-transaction-icon ${row.income ? 'in' : 'out'}`}>{row.income ? '+' : '−'}</span>
                                <span className='hs-panel-row-text'>
                                    <span className='hs-panel-row-title'>{describe(row.description)}</span>
                                    <span className='hs-panel-row-sub'>{new Date(row.date).toLocaleString('sr-RS', { dateStyle: 'short', timeStyle: 'short' })}</span>
                                </span>
                                <span className={`hs-panel-transaction-amount ${row.income ? 'in' : 'out'}`}>{row.income ? '+' : '−'}{money(row.amount)} RSD</span>
                            </div>
                        ))}
                    </div>
                </div>}

            <PanelPagination page={page} pageCount={pageCount} onChange={setPage} />
        </section>
    );
};

export default TransactionPage;
